<template>
  <div v-if="open" class="overlay" @click.self="$emit('close')">
    <div class="modal" role="dialog" aria-modal="true" aria-labelledby="invite-link-title">
      <div class="modal-head">
        <div>
          <div class="eyebrow">Invitación</div>
          <h3 id="invite-link-title">Comparte tu enlace</h3>
          <p class="subtitle">
            <template v-if="groupName">
              Invita personas a <strong>{{ groupName }}</strong>.
            </template>
            <template v-else>
              Comparte este enlace para unirse al grupo.
            </template>
          </p>
        </div>
        <button class="close-btn" type="button" @click="$emit('close')" aria-label="Cerrar">×</button>
      </div>

      <div class="card-box">
        <div class="field-label">Enlace de invitación</div>
        <textarea
          ref="linkField"
          class="link-box"
          :value="link"
          readonly
          spellcheck="false"
          autocapitalize="off"
          autocomplete="off"
          @focus="selectAll"
          @click="selectAll"
        ></textarea>
        <p class="hint">
          En algunos celulares o PWAs el copiado automático puede fallar. Si pasa, mantén presionado el enlace para copiarlo manualmente.
        </p>
      </div>

      <div v-if="statusMessage" class="status-box" :class="statusToneClass">
        {{ statusMessage }}
      </div>

      <div class="actions">
        <button class="primary-btn" type="button" @click="$emit('copy')">Copiar enlace</button>
        <button v-if="canShare" class="secondary-btn" type="button" @click="$emit('share')">Compartir</button>
        <button class="ghost-btn" type="button" @click="selectAll">Seleccionar texto</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InviteLinkModal',
  props: {
    open: { type: Boolean, default: false },
    link: { type: String, default: '' },
    groupName: { type: String, default: '' },
    statusMessage: { type: String, default: '' },
    statusTone: { type: String, default: 'neutral' },
    canShare: { type: Boolean, default: false },
  },
  emits: ['close', 'copy', 'share'],
  computed: {
    statusToneClass() {
      return {
        success: this.statusTone === 'success',
        error: this.statusTone === 'error',
      };
    },
  },
  watch: {
    open(value) {
      if (value) {
        this.$nextTick(() => this.selectAll());
      }
    },
  },
  methods: {
    selectAll() {
      const field = this.$refs.linkField;
      if (!field) return;
      field.focus();
      field.select();
      field.setSelectionRange?.(0, this.link.length || 0);
    },
  },
};
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  z-index: 70;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.42);
  backdrop-filter: blur(10px);
}
.modal {
  width: min(640px, 100%);
  max-height: min(86vh, 900px);
  overflow: auto;
  border-radius: 28px;
  padding: 22px;
  background:
    linear-gradient(180deg, rgba(255,255,255,0.96), rgba(248,250,252,0.98));
  border: 1px solid rgba(148, 163, 184, 0.22);
  box-shadow: 0 30px 80px rgba(15, 23, 42, 0.24);
}
.modal-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}
.eyebrow {
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}
h3 {
  margin: 6px 0 6px;
  font-size: clamp(26px, 4vw, 32px);
  line-height: 1.02;
  color: #0f172a;
}
.subtitle {
  margin: 0;
  color: #475569;
  line-height: 1.5;
}
.close-btn {
  width: 42px;
  height: 42px;
  border: none;
  border-radius: 999px;
  background: rgba(226, 232, 240, 0.7);
  color: #0f172a;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
}
.card-box {
  margin-top: 18px;
  padding: 16px;
  border-radius: 22px;
  background: rgba(255,255,255,0.75);
  border: 1px solid rgba(148, 163, 184, 0.18);
}
.field-label {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}
.link-box {
  width: 100%;
  min-height: 116px;
  margin-top: 10px;
  border: 1px solid rgba(148, 163, 184, 0.26);
  border-radius: 18px;
  padding: 14px 16px;
  resize: vertical;
  background: rgba(248, 250, 252, 0.95);
  color: #0f172a;
  font-size: 15px;
  line-height: 1.55;
}
.hint {
  margin: 10px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: #64748b;
}
.status-box {
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 16px;
  background: rgba(241, 245, 249, 0.9);
  border: 1px solid rgba(148, 163, 184, 0.2);
  color: #334155;
}
.status-box.success {
  background: rgba(236, 253, 245, 0.92);
  border-color: rgba(16, 185, 129, 0.22);
  color: #065f46;
}
.status-box.error {
  background: rgba(254, 242, 242, 0.92);
  border-color: rgba(248, 113, 113, 0.22);
  color: #991b1b;
}
.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 18px;
}
.primary-btn,
.secondary-btn,
.ghost-btn {
  border: none;
  border-radius: 999px;
  padding: 12px 18px;
  font-weight: 800;
  cursor: pointer;
}
.primary-btn {
  background: #111827;
  color: #fff;
}
.secondary-btn {
  background: rgba(224, 231, 255, 0.9);
  color: #312e81;
}
.ghost-btn {
  background: rgba(241, 245, 249, 0.96);
  color: #334155;
}
@media (max-width: 640px) {
  .overlay {
    padding: 14px;
    align-items: end;
  }
  .modal {
    border-radius: 24px 24px 18px 18px;
    padding: 18px;
  }
  .actions {
    display: grid;
    grid-template-columns: 1fr;
  }
  .primary-btn,
  .secondary-btn,
  .ghost-btn {
    width: 100%;
  }
}
</style>
